import { z } from 'zod'

// Price per unit object for length-based pricing
const pricePerUnit = z.object({
  ft: z.number(),
  m: z.number(),
  in: z.number(),
  cm: z.number(),
  mm: z.number(),
})

// Cable type prices (standard, armored, white, red)
export const cableTypePrice = z.object({
  type: z.literal('cable'),
  name: z.enum(['standard', 'armored', 'white', 'red']),
  price: pricePerUnit,
})

// Conduit prices (flex, pex, pexSand, hdpe)
export const conduitPrice = z.object({
  type: z.literal('conduit'),
  name: z.enum(['flex', 'pex', 'pexSand', 'hdpe']),
  price: pricePerUnit,
})

// Cable connector prices (xlr, raymo, bare)
export const cableConnectorPrice = z.object({
  type: z.literal('connector'),
  name: z.enum(['xlr', 'raymo', 'bare', 'subconn']),
  price: z.number(),
})

// Cable heatshrink prices (black, white, none)
export const cableHeatshrinkPrice = z.object({
  type: z.literal('heatshrink'),
  name: z.enum(['black', 'white', 'none']),
  price: z.number(),
})

// Cable packaging prices
export const cablePackagingPrice = z.object({
  type: z.literal('packaging'),
  name: z.enum([
    'Cardboard Box',
    'Pallet',
    'Action Packer',
    'Wooden Spool on Pallet',
    'Plastic Spool on Pallet',
    'Small Wooden Spool (no pallet)',
    'Medium Black Plastic Spool (no pallet)',
  ]),
  price: z.number(),
})

// Fittings prices (pex, flex)
export const fittingsPrice = z.object({
  type: z.literal('fittings'),
  name: z.enum(['pex', 'flex']),
  price: z.number(),
})

// Sensor price - per mold type
export const sensorMoldName = z.enum([
  'none',
  '90degree',
  'end',
  '9mm',
  '11mm',
  '14mm',
  '25mm',
  'd_ring',
])

export type SensorMoldName = z.infer<typeof sensorMoldName>

export const sensorPrice = z.object({
  type: z.literal('sensor'),
  name: sensorMoldName,
  price: z.number(),
})

// MLink price
export const mlinkPrice = z.object({
  type: z.literal('mlink'),
  name: z.literal('mlink'),
  price: z.number(),
})

export const lengthPrices = z.array(z.union([cableTypePrice, conduitPrice]))
export const unitPrices = z.array(
  z.union([
    cableConnectorPrice,
    cableHeatshrinkPrice,
    cablePackagingPrice,
    fittingsPrice,
    sensorPrice,
    mlinkPrice,
  ]),
)

export type LengthPricesColumn = z.infer<typeof lengthPrices>
export type UnitPricesColumn = z.infer<typeof unitPrices>

// Type exports
export type CableTypePrices = z.infer<typeof cableTypePrice>
export type ConduitPrices = z.infer<typeof conduitPrice>
export type CableConnectorPrices = z.infer<typeof cableConnectorPrice>
export type CableHeatshrinkPrices = z.infer<typeof cableHeatshrinkPrice>
export type CablePackagingPrices = z.infer<typeof cablePackagingPrice>
export type FittingsPrices = z.infer<typeof fittingsPrice>
export type SensorPrice = z.infer<typeof sensorPrice>
export type MLinkPrice = z.infer<typeof mlinkPrice>
